import axios from 'axios';
import type { PagoClienteResponse } from '@aid/shared';
import { parseDateOnly } from '../../../lib/date';
import type {
  PagoTableFilterState,
  PagosTableGroup,
  PagosTableGroupBy,
  PagosTableQueryFilters,
  PagosTableScope,
  PagosTranslate,
} from '../components/pago-table-types';
import { PAGOS_TABLE_GROUP, PAGOS_TABLE_SCOPE } from '../components/pago-table-types';

const COBRADOR_WORKLIST_NOT_FOUND_MESSAGE = 'Client is not in the current cobrador worklist';

export function isClientOutsideCobradorWorklistError(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 404) return false;

  const responseData: unknown = error.response.data;
  if (typeof responseData !== 'object' || responseData === null) return false;
  if (!('message' in responseData)) return false;

  return responseData.message === COBRADOR_WORKLIST_NOT_FOUND_MESSAGE;
}

export function esVisita(pago: PagoClienteResponse): boolean {
  return Number.parseFloat(pago.valor) === 0;
}

/** Prefer the derived application range when it differs from the payment range. */
export function rangoCubierto(record: PagoClienteResponse): [string | null, string | null] {
  const aplicaciones = record.aplicacionesCount ?? 0;
  if (aplicaciones > 0 && record.periodoCubiertoInicio && record.periodoCubiertoFin) {
    // Date-only contract: parse component-wise so legacy ISO-midnight values
    // cannot shift the displayed coverage range a day back.
    const derivadoInicio = parseDateOnly(record.periodoCubiertoInicio);
    const derivadoFin = parseDateOnly(record.periodoCubiertoFin);
    const propioInicio = record.fechaInicio ? parseDateOnly(record.fechaInicio) : null;
    const propioFin = record.fechaFin ? parseDateOnly(record.fechaFin) : null;
    const difiere =
      !propioInicio ||
      !propioFin ||
      !derivadoInicio!.isSame(propioInicio, 'day') ||
      !derivadoFin!.isSame(propioFin, 'day');
    if (difiere) return [record.periodoCubiertoInicio, record.periodoCubiertoFin];
  }
  return [record.fechaInicio, record.fechaFin];
}

export function getPagoInitials(nombre: string | null | undefined): string {
  const words = nombre?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (words.length === 0) return '#';
  return words
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('');
}

export function buildPagosQueryFilters(
  scope: PagosTableScope,
  filters: PagoTableFilterState,
  clienteId?: number,
): PagosTableQueryFilters {
  const queryFilters: PagosTableQueryFilters = {};
  const typedClientId = scope === PAGOS_TABLE_SCOPE.CLIENTE ? clienteId : Number(filters.idCliente);
  const hasClientId = scope === PAGOS_TABLE_SCOPE.CLIENTE || filters.idCliente.trim() !== '';

  if (hasClientId && Number.isInteger(typedClientId)) queryFilters.idCliente = typedClientId;
  if (filters.idEstadoPago !== undefined) queryFilters.idEstadoPago = filters.idEstadoPago;
  if (filters.idTipoPago !== undefined) queryFilters.idTipoPago = filters.idTipoPago;
  if (filters.idUsuario !== undefined) queryFilters.idUsuario = filters.idUsuario;
  if (filters.fechaDesde !== undefined) queryFilters.fechaDesde = filters.fechaDesde;
  if (filters.fechaHasta !== undefined) queryFilters.fechaHasta = filters.fechaHasta;

  return queryFilters;
}

export function groupPagos(
  rows: PagoClienteResponse[],
  groupBy: PagosTableGroupBy,
  t: PagosTranslate,
): PagosTableGroup[] | null {
  if (groupBy === PAGOS_TABLE_GROUP.NONE) return null;

  const groups = rows.reduce((groupMap, pago) => {
    const label =
      groupBy === PAGOS_TABLE_GROUP.CLIENTE
        ? pago.cliente
          ? `${pago.cliente.nombre ?? ''} (#${pago.cliente.id})`
          : `#${pago.idCliente}`
        : pago.motivoAnulacion
          ? t('pages.pagos.estadoAnulado')
          : t('pages.pagos.estadoVigente');
    const groupRows = groupMap.get(label);
    if (groupRows) groupRows.push(pago);
    else groupMap.set(label, [pago]);
    return groupMap;
  }, new Map<string, PagoClienteResponse[]>());

  return Array.from(groups.entries())
    .sort(([left], [right]) => left.localeCompare(right, 'es'))
    .map(([label, groupedRows]) => ({ label, rows: groupedRows }));
}
